/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { ListeTable } from '@mairistem/liste';
import { Segment } from '@mairistem/composants';

import type { Pacs } from '@/types/Pacs';
import { StatutPacs } from '@/constants';
import COLORS from '@/constants/colors';
import { AssistantContext } from '@/contexts';
import { canDeclarerSurConvention, formatNomClassement } from '@/utils';

/**
 * Une déclaration non enregistrée au registre ne peut pas recevoir de
 * modificative / dissolution : la ligne est grisée
 */
const CELL_STYLE_NON_ENREGISTRE: React.CSSProperties = {
  color: '#9a9a9a',
  backgroundColor: '#f5f5f5',
  fontStyle: 'italic',
};

const formatPartenaireLibelle = (
  sexe: string | null | undefined,
  nomClassement: string,
): string => {
  const sexeLibelle = sexe && sexe !== 'Non renseigné' ? `${sexe} ` : '';
  return `${sexeLibelle}${_.toUpper(nomClassement)}`;
};

interface PacsListeTableRowProps {
  item: Partial<Pacs>;
  invalidate?: () => void;
  mode?: string;
  dashboard: boolean;
  modal?: boolean;
  stats?: boolean;
  assistant?: boolean;
  selected?: boolean;
  'data-testid'?: string;
}

const PacsListeTableRow = (props: PacsListeTableRowProps) => {
  const {
    item,
    invalidate,
    mode,
    dashboard,
    modal,
    stats,
    assistant,
    selected,
    'data-testid': dataTestId = 'pacs-liste',
  } = props;

  const assistantContext = React.useContext(AssistantContext);
  const selectedDeclarationType = assistantContext?.selectedDeclarationType;

  // Si stats == true, alors on reçoit une convention déclaration avec le pacs lié imbriqué dedans,
  // sinon on reçoit un pacs avec sa dernière convention déclaration imbriquée dedans
  const anneeRegistre = stats
    ? (item as any)?.pacs?.anneeRegistre
    : item?.anneeRegistre;
  const numeroRegistre = stats
    ? (item as any)?.pacs?.numeroRegistre
    : item?.numeroRegistre;
  const libelleRegistre = stats
    ? (item as any)?.pacs?.libelleRegistre
    : item?.libelleRegistre;
  const numero = stats ? (item as any)?.pacs?.numero : item?.numero;

  const partenaire1NomClassement = formatNomClassement(
    stats
      ? (item as any)?.partenaire1NomClassement
      : item?.derniereConvention?.partenaire1NomClassement,
  );
  const partenaire2NomClassement = formatNomClassement(
    stats
      ? (item as any)?.partenaire2NomClassement
      : item?.derniereConvention?.partenaire2NomClassement,
  );
  const partenaire1Sexe = stats
    ? (item as any)?.partenaire1Sexe
    : item?.derniereConvention?.partenaire1Sexe;
  const partenaire2Sexe = stats
    ? (item as any)?.partenaire2Sexe
    : item?.derniereConvention?.partenaire2Sexe;
  const statut = stats
    ? (item as any)?.statut
    : item?.derniereConvention?.statut;

  const cellStyleNonEnregistre = React.useMemo(() => {
    const convention = stats ? (item as any) : item?.derniereConvention;

    return canDeclarerSurConvention(selectedDeclarationType, convention)
      ? undefined
      : CELL_STYLE_NON_ENREGISTRE;
  }, [item, stats, selectedDeclarationType]);

  const getStatusColor = React.useCallback((s?: StatutPacs): string => {
    switch (s) {
      case StatutPacs.PROJET:
        return COLORS.BlueListColor;
      case StatutPacs.EN_COURS:
        return COLORS.GreenListColor;
      case StatutPacs.DISSOUS:
        return COLORS.RedListColor;
      case StatutPacs.IRRECEVABLE:
        return COLORS.PurpleListColor;
      default:
        return COLORS.GreyDashboardColor;
    }
  }, []);

  // Sert à redimensionner les lignes comme il faut quand on passe
  // de la grande liste à la liste dockée
  // biome-ignore lint/correctness/useExhaustiveDependencies: no need
  React.useEffect(() => {
    invalidate?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const renderDashboardRow = React.useMemo(
    () => (
      <>
        <ListeTable.Cell
          name="partenaires"
          data-testid={`${dataTestId}-cellule-partenaires`}
        >
          {`${formatPartenaireLibelle(partenaire1Sexe, partenaire1NomClassement)} et ${formatPartenaireLibelle(partenaire2Sexe, partenaire2NomClassement)}`}
        </ListeTable.Cell>
        <ListeTable.Cell
          name="typePacs"
          data-testid={`${dataTestId}-cellule-type-pacs`}
        >
          <Segment
            compact
            content={statut}
            style={{
              backgroundColor: getStatusColor(statut),
              color: 'white',
              minWidth: '6em',
              maxWidth: '6em',
              paddingTop: 0,
              paddingBottom: 0,
            }}
          />
        </ListeTable.Cell>
      </>
    ),
    [
      partenaire1NomClassement,
      partenaire1Sexe,
      partenaire2NomClassement,
      partenaire2Sexe,
      statut,
      getStatusColor,
      dataTestId,
    ],
  );

  const renderAssistantRow = React.useMemo(
    () => (
      <>
        <ListeTable.Cell
          name="annee"
          data-testid={`${dataTestId}-cellule-annee`}
          style={cellStyleNonEnregistre}
        >
          {anneeRegistre}
        </ListeTable.Cell>
        <ListeTable.Cell
          name="registre"
          data-testid={`${dataTestId}-cellule-registre`}
          style={cellStyleNonEnregistre}
        >
          {`Registre ${numeroRegistre} des ${_.toUpper(libelleRegistre)}`}
        </ListeTable.Cell>
        <ListeTable.Cell
          name="partenaire1"
          data-testid={`${dataTestId}-cellule-partenaire-1`}
          style={cellStyleNonEnregistre}
        >
          {partenaire1NomClassement}
        </ListeTable.Cell>
        <ListeTable.Cell
          name="partenaire2"
          data-testid={`${dataTestId}-cellule-partenaire-2`}
          style={cellStyleNonEnregistre}
        >
          {partenaire2NomClassement}
        </ListeTable.Cell>
      </>
    ),
    [
      anneeRegistre,
      partenaire1NomClassement,
      partenaire2NomClassement,
      numeroRegistre,
      libelleRegistre,
      cellStyleNonEnregistre,
      dataTestId,
    ],
  );

  const renderRow = React.useMemo(
    () => (
      <>
        {!modal && (
          <>
            <ListeTable.Cell
              name="annee"
              data-testid={`${dataTestId}-cellule-annee`}
            >
              {anneeRegistre}
            </ListeTable.Cell>
            <ListeTable.Cell
              name="registre"
              data-testid={`${dataTestId}-cellule-registre`}
            >
              {`Registre ${numeroRegistre} des ${_.toUpper(libelleRegistre)}`}
            </ListeTable.Cell>
          </>
        )}
        <ListeTable.Cell
          name="numero"
          data-testid={`${dataTestId}-cellule-numero`}
        >
          {numero}
        </ListeTable.Cell>
        <ListeTable.Cell
          name="statut"
          data-testid={`${dataTestId}-cellule-statut`}
        >
          {statut}
        </ListeTable.Cell>
        <ListeTable.Cell
          name="partenaire1"
          data-testid={`${dataTestId}-cellule-partenaire-1`}
        >
          {partenaire1NomClassement}
        </ListeTable.Cell>
        <ListeTable.Cell
          name="partenaire2"
          data-testid={`${dataTestId}-cellule-partenaire-2`}
        >
          {partenaire2NomClassement}
        </ListeTable.Cell>
      </>
    ),
    [
      anneeRegistre,
      numero,
      partenaire1NomClassement,
      partenaire2NomClassement,
      statut,
      numeroRegistre,
      libelleRegistre,
      modal,
      dataTestId,
    ],
  );

  // On ajoute une ligne proposant la création d'une nouvelle
  // déclaration uniquement si mode sélectionné est initiale ou projet
  if ((item as any).isAssistantEmptyRow) {
    if (
      selectedDeclarationType === 'initiale' ||
      selectedDeclarationType === 'projet'
    ) {
      let emptyRowContent: string;
      switch (selectedDeclarationType) {
        case 'initiale':
          emptyRowContent =
            "Création d'une déclaration initiale sans rattachement à un projet existant";
          break;
        default:
          emptyRowContent =
            "Création d'un nouveau projet sans rattachement à un projet existant";
      }

      return (
        <ListeTable.Row
          key="empty-row"
          selected={selected}
          className="assistant-empty-row"
        >
          <td
            colSpan={4}
            className="assistant-empty-cell"
            data-testid={`${dataTestId}-option-creation`}
          >
            {emptyRowContent}
          </td>
        </ListeTable.Row>
      );
    }
    return null;
  }

  return (
    <ListeTable.Row
      key={stats ? item.identifiant : item.derniereConvention.identifiant}
      selected={selected}
      data-testid={`${dataTestId}-ligne`}
    >
      {assistant
        ? renderAssistantRow
        : dashboard
          ? renderDashboardRow
          : renderRow}
    </ListeTable.Row>
  );
};

export default PacsListeTableRow;
